import { useMemo, useState } from 'react';
import {
  api, type AddableOffer, type CommentTemplate, type OrderDetail, type OrderEdit, type OrderLine, type OrderTag,
} from '../api';
import { label, money, unit, useDebounced, useLoad } from '../lib';

// One order, as staff work it on the Adobe order screen (parity review 2026-10-16, Tier 1 #1): its lines
// with vendor SKU, brand, pack, status, qty, received, price and delivery date; editing them (qty, price,
// qty received, remove, add a product, a date per line or one for the whole order); comments from saved templates; tags; the
// notification mute; the status. Commerce runs every rule and audits every write — this screen shows
// the vendor's guidance so staff see a warning before they save, never instead of the server's answer.

const DAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const weekdayOf = (iso: string) => new Date(`${iso}T00:00:00Z`).getUTCDay();
const usDate = (iso: string | null | undefined) => (iso ? `${iso.slice(5, 7)}/${iso.slice(8, 10)}/${iso.slice(0, 4)}` : '—');
const dayDate = (iso: string | null | undefined) => (iso ? `${DAYS[weekdayOf(iso)]} ${usDate(iso)}` : '—');
const qtyText = (n: number | null | undefined) => (n == null ? '—' : Number.isInteger(n) ? String(n) : String(Math.round(n * 1e4) / 1e4));
const pack = (l: { pack_count: number | null; pack_size: number | null; uom: string | null }) =>
  l.pack_size == null && l.uom == null ? '—' : `${qtyText(l.pack_count ?? 1)} / ${qtyText(l.pack_size)} ${l.uom ?? ''}`.trim();

/** Magento's history comments are HTML ("<strong>Item Changes:</strong><br/>…"); shown as plain text. */
export const plainComment = (c: string | null) =>
  (c ?? '')
    .replace(/<br\s*\/?>/gi, '\n')
    .replace(/<[^>]+>/g, '')
    .replace(/&nbsp;/g, ' ').replace(/&amp;/g, '&').replace(/&lt;/g, '<').replace(/&gt;/g, '>').replace(/&quot;/g, '"').replace(/&#039;/g, "'")
    .trim();

/** The change log names columns; these read better as the screen's own words. */
const FIELD_LABEL: Record<string, string> = { qty_ordered: 'Qty', qty_override: 'Qty received' };
const fieldLabel = (f: string) => FIELD_LABEL[f] ?? label(f);

/** A change-log value: a removed or added line is stored whole, as JSON — shown as the line it was. */
const changeValue = (v: string | null) => {
  if (v == null) return '—';
  if (!v.startsWith('{')) return v;
  try {
    const l = JSON.parse(v) as { name?: string; supplierSku?: string; qty?: number; price?: number; deliveryDate?: string };
    return `${l.name ?? 'item'}${l.supplierSku ? ` (${l.supplierSku})` : ''} × ${qtyText(l.qty)} @ ${unit(l.price)}${l.deliveryDate ? ` · ${usDate(l.deliveryDate)}` : ''}`;
  } catch {
    return v;
  }
};

// price and received are the inputs' text: '' received = not set (the row is priced on qty ordered).
type LineDraft = { qty: string; price: string; received: string; date: string; remove: boolean };
type AddDraft = { offer: AddableOffer; qty: string };

export function OrderDetailView({ id, canEdit, onBack }: { id: number; canEdit: boolean; onBack: () => void }) {
  const { data, err, reload } = useLoad(() => api.commerceOrderDetail(id), [id]);
  const [msg, setMsg] = useState<{ good: boolean; text: string; extra?: string[]; warn?: string[] } | null>(null);
  const [busy, setBusy] = useState(false);

  async function act(fn: () => Promise<string | { text: string; extra?: string[]; warn?: string[] }>) {
    setBusy(true);
    setMsg(null);
    try {
      const r = await fn();
      setMsg(typeof r === 'string' ? { good: true, text: r } : { good: true, ...r });
      reload();
    } catch (e) {
      setMsg({ good: false, text: (e as Error).message });
    } finally {
      setBusy(false);
    }
  }

  if (err) return <div className="stack"><BackBar onBack={onBack} /><p className="msg bad">⚠ {err}</p></div>;
  if (!data) return <div className="stack"><BackBar onBack={onBack} /><p className="loading">Loading order…</p></div>;
  const o = data.order;

  return (
    <div className="stack">
      <BackBar onBack={onBack} />
      <div className="od-head">
        <h1>PO {o.po_number ?? o.increment_id}</h1>
        <span className="pill type">{label(o.status)}</span>
        {o.notifications_muted && <span className="pill">Notifications muted</span>}
        {data.tags.map((t) => <span key={t.id} className="pill tag">{t.name}</span>)}
      </div>
      <p className="sub">{o.camp ?? o.customer_name ?? '—'} · {o.vendor ?? '—'} · placed {o.created_at?.slice(0, 16) ?? '—'}{o.placed_by_name || o.customer_email ? ` by ${o.placed_by_name ?? o.customer_email}` : ''}</p>
      {o.adobeOwned && (
        <p className="note">This order came from Adobe, which is still the system of record. A change made here lasts until the next data load unless it is also made in Adobe.</p>
      )}
      {msg && (
        <div className={`msg ${msg.good ? 'good' : 'bad'}`}>
          {msg.good ? '' : '⚠ '}{msg.text}
          {msg.extra && msg.extra.length > 0 && <ul className="od-list">{msg.extra.map((x, i) => <li key={i}>{x}</li>)}</ul>}
        </div>
      )}
      {msg?.warn && msg.warn.length > 0 && <div className="note">{msg.warn.map((x, i) => <div key={i}>{x}</div>)}</div>}

      <OrderSummary d={data} canEdit={canEdit} busy={busy} act={act} />
      <LinesCard d={data} canEdit={canEdit} busy={busy} act={act} />
      <div className="od-cols">
        <CommentsCard d={data} canEdit={canEdit} busy={busy} act={act} />
        <TagsCard d={data} canEdit={canEdit} busy={busy} act={act} />
      </div>
      <HistoryCard d={data} />
    </div>
  );
}

function BackBar({ onBack }: { onBack: () => void }) {
  return <div className="toolbar"><button className="btn-sm ghost" onClick={onBack}>← Order Queue</button></div>;
}

type Act = (fn: () => Promise<string | { text: string; extra?: string[]; warn?: string[] }>) => Promise<void>;
type CardProps = { d: OrderDetail; canEdit: boolean; busy: boolean; act: Act };

function OrderSummary({ d, canEdit, busy, act }: CardProps) {
  const o = d.order;
  const r = d.rules;
  const ship = d.shipTo;
  return (
    <section className="card">
      <div className="od-facts">
        <div><span className="iw-lbl">Total</span><div>{money(o.grand_total)}</div></div>
        <div><span className="iw-lbl">Delivery</span><div>{dayDate(o.delivery_date)}</div></div>
        <div><span className="iw-lbl">Type</span><div>{o.attribute_set_type ?? '—'}</div></div>
        <div><span className="iw-lbl">GL code</span><div>{o.gl_codes ?? '—'}</div></div>
        <div><span className="iw-lbl">Vendor acct</span><div>{o.customer_acct ?? '—'}</div></div>
        <div>
          <span className="iw-lbl">Ship to</span>
          <div className="addr-lines">{ship ? [ship.company, ship.street, [ship.city, ship.region, ship.postcode].filter(Boolean).join(', ')].filter(Boolean).map((x, i) => <span key={i}>{x}</span>) : '—'}</div>
        </div>
      </div>
      <p className="muted od-rules">
        {o.vendor ?? 'The vendor'} delivers {r.allowedWeekdays.length === 7 ? 'any day' : r.allowedWeekdays.map((n) => DAYS[n]).join('/')}
        {r.earliest ? ` · earliest from today ${dayDate(r.earliest)}` : ''}
        {r.cutoffTime ? ` · cutoff ${r.cutoffTime}` : ''}
        {r.deliveryDateRequired ? ' · a delivery date is required' : ''}
        {r.minimum.applicable ? ` · minimum ${r.minOrderAmount ? money(r.minOrderAmount) : `${r.minOrderQty} units`} ${r.minimum.meets ? '(met)' : `(short ${r.minimum.shortfallAmount != null ? money(r.minimum.shortfallAmount) : `${r.minimum.shortfallQty} units`})`}` : ''}
      </p>
      {canEdit && (
        <div className="toolbar">
          {o.allowedNext.length > 0 && (
            <select className="sel" aria-label="Change status" disabled={busy} defaultValue="" key={o.status}
              onChange={(e) => {
                const to = e.target.value;
                if (to) void act(async () => { await api.commerceSetOrderStatus(o.id, to); return `Status → ${label(to)}`; });
              }}>
              <option value="" disabled>Change status…</option>
              {o.allowedNext.map((n) => <option key={n} value={n}>{label(n)}</option>)}
            </select>
          )}
          <button className="btn-sm ghost" disabled={busy}
            title="Adobe's Mute Order Notification: the order is skipped by the below-minimum delivery alert"
            onClick={() => act(async () => { await api.commerceMuteOrder(o.id, !o.notifications_muted); return o.notifications_muted ? 'Notifications unmuted' : 'Notifications muted'; })}>
            {o.notifications_muted ? 'Unmute notifications' : 'Mute notifications'}
          </button>
          {o.status === 'pending' && (
            <button className="btn-sm ghost" disabled={busy}
              onClick={() => act(async () => { const r = await api.commerceResendPo(o.id); return `PO email: ${r.row?.status ?? r.status ?? 'sent'}${r.row?.error ? ` — ${r.row.error}` : ''}`; })}>
              Resend PO to vendor
            </button>
          )}
        </div>
      )}
    </section>
  );
}

function LinesCard({ d, canEdit, busy, act }: CardProps) {
  const o = d.order;
  const [editing, setEditing] = useState(false);
  const [drafts, setDrafts] = useState<Record<number, LineDraft>>({});
  const [adds, setAdds] = useState<AddDraft[]>([]);
  const [orderDate, setOrderDate] = useState('');
  const [comment, setComment] = useState('');
  const [override, setOverride] = useState(false);
  const lock = d.editLock;

  const fresh = (l: OrderLine): LineDraft => ({
    qty: String(l.qty_ordered), price: String(l.price), received: l.qty_override == null ? '' : String(l.qty_override),
    date: l.delivery_date ?? '', remove: false,
  });
  const start = () => {
    setDrafts(Object.fromEntries(d.lines.map((l) => [l.id, fresh(l)])));
    setAdds([]); setOrderDate(''); setComment(''); setOverride(false); setEditing(true);
  };
  const draftOf = (l: OrderLine) => drafts[l.id] ?? fresh(l);
  const receivedOf = (x: LineDraft) => (x.received.trim() === '' ? null : Number(x.received));
  const priceOf = (l: OrderLine, x: LineDraft) => (x.price.trim() === '' ? l.price : Number(x.price) || 0);
  const setDraft = (id: number, patch: Partial<LineDraft>) => setDrafts((all) => ({ ...all, [id]: { ...all[id]!, ...patch } }));

  // What the save would send: only what differs from the order as loaded.
  const edit: OrderEdit = useMemo(() => ({
    version: o.version,
    lines: d.lines.flatMap((l) => {
      const x = draftOf(l);
      if (x.remove) return [{ lineId: l.id, remove: true }];
      const out: OrderEdit['lines'][number] = { lineId: l.id };
      if (Number(x.qty) !== l.qty_ordered) out.qty = Number(x.qty);
      // Sent as typed, so commerce's refusal names a bad value instead of the screen guessing one.
      if (x.price.trim() === '' || Number(x.price) !== l.price) out.price = x.price.trim();
      if (receivedOf(x) !== l.qty_override) out.qtyReceived = x.received.trim() === '' ? null : x.received.trim();
      if (!orderDate && x.date !== (l.delivery_date ?? '')) out.deliveryDate = x.date;
      return Object.keys(out).length > 1 ? [out] : [];
    }),
    adds: adds.map((a) => ({ offerId: a.offer.offer_id, qty: Number(a.qty) })),
    ...(orderDate ? { deliveryDate: orderDate } : {}),
    ...(comment.trim() ? { comment: comment.trim() } : {}),
    ...(override ? { opsOverride: true } : {}),
  }), [drafts, adds, orderDate, comment, override, d]);
  const dirty = edit.lines.length > 0 || edit.adds.length > 0 || !!edit.deliveryDate;
  const estimate = d.lines.reduce((s, l) => {
    const x = draftOf(l);
    return x.remove ? s : s + priceOf(l, x) * (receivedOf(x) ?? (Number(x.qty) || 0));
  }, 0) + adds.reduce((s, a) => s + a.offer.price * (Number(a.qty) || 0), 0);
  const dateHint = (iso: string) => {
    if (!iso) return d.rules.deliveryDateRequired ? 'a date is required' : null;
    if (!d.rules.allowedWeekdays.includes(weekdayOf(iso))) return `${DAYS[weekdayOf(iso)]} is not a delivery day`;
    if (d.rules.earliest && iso < d.rules.earliest) return `before the earliest date (${usDate(d.rules.earliest)})`;
    return null;
  };

  async function save() {
    await act(async () => {
      const r = await api.commerceEditOrder(o.id, edit);
      setEditing(false);
      const warn = r.overrides.map((x) => `Saved outside ${o.vendor ?? 'the vendor'}'s guidelines, and recorded: ${x}`);
      if (r.vendorHasOldPo) warn.push('The vendor already has this PO — resend it if they need the change.');
      return { text: `Saved — new total ${money(r.totals.subtotal)}`, extra: r.history, warn };
    });
  }

  return (
    <section className="card">
      <div className="od-card-head">
        <h2>Items ({d.lines.length})</h2>
        {canEdit && !editing && (
          lock.allowed || lock.reason === 'invoiced'
            ? <button className="btn-sm" disabled={busy} onClick={start}>Edit items</button>
            : <span className="muted small">{lock.message}</span>
        )}
      </div>
      {editing && !lock.allowed && lock.reason === 'invoiced' && (
        <p className="note">{lock.message}. <label className="chk"><input type="checkbox" checked={override} onChange={(e) => setOverride(e.target.checked)} /> Use the ops override</label></p>
      )}
      <div className="od-scroll">
        <table>
          <thead>
            <tr><th>Vendor SKU</th><th>Item</th><th>Pack</th><th>Status</th><th className="num">Qty</th><th className="num" title="What arrived — when set, the line is priced on it">Received</th><th className="num">Price</th><th className="num">Total</th><th>Delivery</th>{editing && <th>Remove</th>}</tr>
          </thead>
          <tbody>
            {d.lines.map((l) => {
              const x = draftOf(l);
              const hint = editing && !x.remove && !orderDate ? dateHint(x.date) : null;
              return (
                <tr key={l.id} className={x.remove && editing ? 'inactive' : ''}>
                  <td className="mono">{l.supplier_sku ?? l.sku ?? '—'}</td>
                  <td>{l.name ?? '—'}{l.brand && <div className="muted small">{l.brand}</div>}</td>
                  <td className="muted">{pack(l)}</td>
                  <td><span className="pill type">{l.status}</span>{l.ack_status && <div className="muted small">ACK {l.ack_status}</div>}</td>
                  <td className="num">
                    {editing && !x.remove
                      ? <input className="iw-in" type="number" min="0" step="any" aria-label={`Qty for ${l.name ?? l.id}`} value={x.qty} onChange={(e) => setDraft(l.id, { qty: e.target.value })} />
                      : qtyText(l.qty_ordered)}
                  </td>
                  <td className="num">
                    {editing && !x.remove
                      ? <input className="iw-in" type="number" min="0" step="1" placeholder="—" aria-label={`Qty received for ${l.name ?? l.id}`} title="Leave empty if not corrected" value={x.received} onChange={(e) => setDraft(l.id, { received: e.target.value })} />
                      : qtyText(l.qty_override)}
                  </td>
                  <td className="num">
                    {editing && !x.remove
                      ? <input className="iw-in" type="number" min="0" step="0.01" aria-label={`Price for ${l.name ?? l.id}`} value={x.price} onChange={(e) => setDraft(l.id, { price: e.target.value })} />
                      : unit(l.price)}
                  </td>
                  <td className="num">{money(editing && !x.remove ? priceOf(l, x) * (receivedOf(x) ?? (Number(x.qty) || 0)) : l.row_total)}</td>
                  <td>
                    {editing && !x.remove && !orderDate
                      ? <input className="sel" type="date" aria-label={`Delivery date for ${l.name ?? l.id}`} value={x.date} onChange={(e) => setDraft(l.id, { date: e.target.value })} />
                      : dayDate(editing && orderDate && !x.remove ? orderDate : l.delivery_date)}
                    {hint && <div className="od-warn small">{hint}</div>}
                  </td>
                  {editing && <td><input type="checkbox" aria-label={`Remove ${l.name ?? l.id}`} checked={x.remove} onChange={(e) => setDraft(l.id, { remove: e.target.checked })} /></td>}
                </tr>
              );
            })}
            {editing && adds.map((a, i) => (
              <tr key={`add-${a.offer.offer_id}`}>
                <td className="mono">{a.offer.supplier_sku ?? '—'}</td>
                <td>{a.offer.name ?? '—'} <span className="pill">new</span>{a.offer.brand && <div className="muted small">{a.offer.brand}</div>}</td>
                <td className="muted">{pack(a.offer)}</td>
                <td />
                <td className="num"><input className="iw-in" type="number" min="0" step="any" aria-label={`Qty for ${a.offer.name ?? a.offer.offer_id}`} value={a.qty} onChange={(e) => setAdds(adds.map((y, j) => (j === i ? { ...y, qty: e.target.value } : y)))} /></td>
                <td className="num muted">—</td>
                <td className="num">{unit(a.offer.price)}</td>
                <td className="num">{money(a.offer.price * (Number(a.qty) || 0))}</td>
                <td className="muted">
                  {dayDate(orderDate || o.delivery_date)}
                  {dateHint(orderDate || o.delivery_date || '') && <div className="od-warn small">{dateHint(orderDate || o.delivery_date || '')}</div>}
                </td>
                <td><button className="link" onClick={() => setAdds(adds.filter((_, j) => j !== i))}>remove</button></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {editing && (
        <div className="od-edit">
          <AddProduct orderId={o.id} vendor={o.vendor} picked={adds.map((a) => a.offer.offer_id)} onPick={(offer) => setAdds([...adds, { offer, qty: '1' }])} />
          <div className="form-row">
            <label className="field">
              <span>One date for the whole order</span>
              <input className="search" type="date" value={orderDate} onChange={(e) => setOrderDate(e.target.value)} />
            </label>
            {orderDate && dateHint(orderDate) && <span className="od-warn small">{dateHint(orderDate)} — the save is recorded as outside the vendor's guidelines</span>}
            {orderDate && <button className="btn-sm ghost" onClick={() => setOrderDate('')}>Use per-item dates</button>}
          </div>
          <label className="field">
            <span>Note for the order history (optional)</span>
            <input className="search wide" value={comment} maxLength={5000} onChange={(e) => setComment(e.target.value)} placeholder="e.g. camp called to add ketchup" />
          </label>
          <div className="edi-actions">
            <span className="muted">New total about {money(estimate)} — commerce re-prices and re-checks the vendor's rules on save.</span>
            <div className="actions">
              <button className="btn-sm ghost" disabled={busy} onClick={() => setEditing(false)}>Cancel</button>
              <button className="btn-sm" disabled={busy || !dirty || (!lock.allowed && !override)} onClick={save}>{busy ? 'Saving…' : 'Save changes'}</button>
            </div>
          </div>
        </div>
      )}
    </section>
  );
}

function AddProduct({ orderId, vendor, picked, onPick }: { orderId: number; vendor: string | null; picked: number[]; onPick: (o: AddableOffer) => void }) {
  const [q, setQ] = useState('');
  const dq = useDebounced(q, 300);
  const { data, err } = useLoad(() => (dq.trim().length >= 2 ? api.commerceOrderOffers(orderId, dq.trim()) : Promise.resolve([])), [orderId, dq]);
  return (
    <div className="field">
      <span>Add a product from {vendor ?? 'this vendor'}</span>
      <input className="search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Name, vendor SKU or brand…" aria-label="Find a product to add" />
      {err && <span className="od-warn small">{err}</span>}
      {dq.trim().length >= 2 && data && (
        <div className="pick-list">
          {data.length === 0 && <span className="muted ta-opt">No orderable product matches.</span>}
          {data.map((x) => (
            <button key={x.offer_id} className={`ta-opt${picked.includes(x.offer_id) ? ' picked' : ''}`} disabled={picked.includes(x.offer_id)}
              onClick={() => { onPick(x); setQ(''); }}>
              <span className="mono">{x.supplier_sku ?? x.sku}</span> {x.name}{x.brand ? ` · ${x.brand}` : ''} · {pack(x)} · {unit(x.price)}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

function CommentsCard({ d, canEdit, busy, act }: CardProps) {
  const [text, setText] = useState('');
  const [notify, setNotify] = useState(false);
  const [managing, setManaging] = useState(false);
  const { data: templates, reload } = useLoad(() => api.commerceCommentTemplates(), []);
  if (!canEdit) return null;
  return (
    <section className="card">
      <div className="od-card-head">
        <h2>Add a comment</h2>
        <button className="link edit" onClick={() => setManaging(!managing)}>{managing ? 'DONE' : 'TEMPLATES'}</button>
      </div>
      {managing ? <TemplateManager onChanged={reload} /> : (
        <div className="od-edit">
          <select className="sel" aria-label="Comment template" value="" onChange={(e) => {
            const t = (templates ?? []).find((x) => x.id === Number(e.target.value));
            if (t) setText(t.comment);
          }}>
            <option value="">Start from a template…</option>
            {(templates ?? []).map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
          </select>
          <textarea className="edi-textarea" rows={5} value={text} maxLength={5000} onChange={(e) => setText(e.target.value)}
            placeholder="Write a comment — replace any XXXX in a template before you save it" aria-label="Comment" />
          {/XXXX|XITEMX/.test(text) && <span className="od-warn small">The template still has a placeholder (XXXX) to fill in.</span>}
          <div className="edi-actions">
            <label className="chk"><input type="checkbox" checked={notify} onChange={(e) => setNotify(e.target.checked)} /> Email it to the camp</label>
            <button className="btn-sm" disabled={busy || !text.trim()} onClick={() => act(async () => {
              const r = await api.commerceOrderComment(d.order.id, text, notify);
              setText(''); setNotify(false);
              return r.emailed === 'sent' ? 'Comment added and emailed to the camp'
                : r.emailed === 'no_recipients' ? 'Comment added — the camp has no buyer email on file, so nothing was sent'
                : r.emailed ? `Comment added — the email was ${r.emailed === 'skipped' ? 'not sent (email is off here)' : 'not sent (the mail provider refused it)'}`
                : 'Comment added';
            })}>Add comment</button>
          </div>
        </div>
      )}
    </section>
  );
}

function TemplateManager({ onChanged }: { onChanged: () => void }) {
  const { data, err, reload } = useLoad(() => api.commerceCommentTemplates(true), []);
  const [draft, setDraft] = useState<{ id?: number; name: string; comment: string; position: string } | null>(null);
  const [msg, setMsg] = useState<string | null>(null);
  async function save(t: Parameters<typeof api.commerceSaveCommentTemplate>[0]) {
    setMsg(null);
    try { await api.commerceSaveCommentTemplate(t); setDraft(null); reload(); onChanged(); }
    catch (e) { setMsg((e as Error).message); }
  }
  if (err) return <p className="msg bad">⚠ {err}</p>;
  return (
    <div className="od-edit">
      {msg && <p className="msg bad">⚠ {msg}</p>}
      {draft ? (
        <div className="od-edit">
          <input className="search" value={draft.name} onChange={(e) => setDraft({ ...draft, name: e.target.value })} placeholder="Template name" aria-label="Template name" />
          <textarea className="edi-textarea" rows={5} value={draft.comment} onChange={(e) => setDraft({ ...draft, comment: e.target.value })} aria-label="Template text" />
          <div className="actions">
            <input className="iw-in" type="number" min="0" value={draft.position} onChange={(e) => setDraft({ ...draft, position: e.target.value })} aria-label="Position" title="Position in the list" />
            <button className="btn-sm ghost" onClick={() => setDraft(null)}>Cancel</button>
            <button className="btn-sm" onClick={() => save({ id: draft.id, name: draft.name, comment: draft.comment, position: Number(draft.position) || 0 })}>Save template</button>
          </div>
        </div>
      ) : <button className="btn-sm ghost" onClick={() => setDraft({ name: '', comment: '', position: '0' })}>New template</button>}
      <table>
        <tbody>
          {(data ?? []).map((t: CommentTemplate) => (
            <tr key={t.id} className={t.is_active ? '' : 'inactive'}>
              <td>{t.name}</td>
              <td className="actions">
                <button className="link edit" onClick={() => setDraft({ id: t.id, name: t.name, comment: t.comment, position: String(t.position) })}>EDIT</button>
                <button className="link edit" onClick={() => save({ id: t.id, isActive: !t.is_active })}>{t.is_active ? 'TURN OFF' : 'TURN ON'}</button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function TagsCard({ d, canEdit, busy, act }: CardProps) {
  const { data: all } = useLoad(() => api.commerceOrderTags(), []);
  const on = new Set(d.tags.map((t) => t.id));
  const toggle = (t: OrderTag) => act(async () => {
    const next = on.has(t.id) ? [...on].filter((x) => x !== t.id) : [...on, t.id];
    await api.commerceSetOrderTags(d.order.id, next);
    return `${on.has(t.id) ? 'Removed' : 'Added'} tag ${t.name}`;
  });
  return (
    <section className="card">
      <h2>Tags</h2>
      <div className="toolbar">
        {(canEdit ? all ?? [] : d.tags).map((t) => (
          <button key={t.id} className={`chip${on.has(t.id) ? ' on' : ''}`} disabled={!canEdit || busy} onClick={() => void toggle(t)}
            aria-pressed={on.has(t.id)}>{t.name}</button>
        ))}
        {!canEdit && d.tags.length === 0 && <span className="muted">No tags.</span>}
      </div>
    </section>
  );
}

function HistoryCard({ d }: { d: OrderDetail }) {
  const [showChanges, setShowChanges] = useState(false);
  const events = [...d.events].reverse();
  return (
    <section className="card">
      <div className="od-card-head">
        <h2>History</h2>
        {d.changes.length > 0 && <button className="link edit" onClick={() => setShowChanges(!showChanges)}>{showChanges ? 'HIDE FIELD CHANGES' : `FIELD CHANGES (${d.changes.length})`}</button>}
      </div>
      {showChanges && (
        <div className="od-scroll">
          <table>
            <thead><tr><th>When</th><th>Who</th><th>What</th><th>From</th><th>To</th></tr></thead>
            <tbody>
              {d.changes.map((c, i) => (
                <tr key={i}>
                  <td className="mono">{c.changed_at.slice(0, 16)}</td><td>{c.changed_by}</td>
                  <td>{fieldLabel(c.field)}{c.entity === 'po_line' ? <span className="muted"> · line {c.entity_id}</span> : ''}</td>
                  <td className="mono od-val">{changeValue(c.old_value)}</td><td className="mono od-val">{changeValue(c.new_value)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {d.overrides.length > 0 && (
        <p className="note">Outside vendor guidelines: {d.overrides.map((x) => x.detail ?? `${x.rule} ${x.reason}`).join('; ')}</p>
      )}
      {events.length === 0 ? <p className="muted">No history recorded for this order.</p> : (
        <ol className="order-history">
          {events.map((e) => (
            <li key={e.id}>
              <span className="mono muted">{e.created_at?.slice(0, 16) ?? '—'}</span> <span className="pill type">{label(e.status)}</span>
              {e.author && <span className="muted small"> · {e.author}</span>}
              {e.notified && <span className="muted small"> · emailed to the camp</span>}
              {e.comment && <div className="order-history-comment">{plainComment(e.comment)}</div>}
            </li>
          ))}
        </ol>
      )}
    </section>
  );
}
